import type { Agent, AnswerContactRequest, Attachment, ChatResponse, CreateAgentRequest, HandoffResolution, RosterAgent, ServerEvent, Settings as HostSettings, SidebarSettings, UpdateAgentRequest } from "@ark-bot/protocol";
import type { SubmitLoginRequest } from "@ark-bot/protocol";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { useAccount } from "./account.js";
import { api, subscribe, subscribeRoster, type ConnectionStatus } from "./api.js";
import { ChatCache, cacheScope, reconcile } from "./chat-cache.js";
import { bundledShell, configuredHost } from "./host.js";
import { useBackClose, useLeaveWarning, useMedia } from "./back.js";
import { text, useText } from "./i18n.js";
import { Avatar, AvatarShapeDefs } from "./components/Avatar.js";
import { Chat } from "./components/Chat.js";
import { Composer } from "./components/Composer.js";
import { Computer } from "./components/Computer.js";
import { ConfirmProvider, useConfirmHost } from "./components/ConfirmDialog.js";
import { Details, type DetailsMode } from "./components/Details.js";
import { Resizer } from "./components/Resizer.js";
import { Connect } from "./components/Connect.js";
import { Plugins } from "./components/Plugins.js";
import { Settings } from "./components/Settings.js";
import { Sidebar } from "./components/Sidebar.js";
import { Switcher } from "./components/Switcher.js";
import { Tour } from "./components/Tour.js";
import { mentionablesFor } from "./mentions.js";
import { isSidebarKey, isSwitcherKey, shortcutLabel } from "./navigation.js";
import { unsettled, type Outgoing } from "./outbox.js";
import { askForNotificationPermission, notificationPermission, raise, subscribeNotifications, type NotificationPermissionState } from "./notifications.js";
import { tourDecision } from "./onboarding.js";
import { firstRegistration, shellPush, shellPushState, type PushWindow } from "./push.js";
import { DETAILS_WIDTH, OVERLAY_QUERY, PHONE_QUERY, SIDEBAR_WIDTH, layoutAt, loadWidth } from "./panes.js";
import { usePresence } from "./presence.js";
import { applyRosterEvent } from "./roster.js";
import { EMPTY_SIDEBAR, addSection, moveToSection, removeSection, renameSection, reorderPinned, reorderSection, toggleHidden, togglePinned } from "./sidebar-settings.js";
import { agentStatus, applyEvent, emptyChat, oldestSeq, pendingHandoff, prependMessages, usingDesktop as agentUsingDesktop, type ChatState } from "./state.js";
import { APP } from "./text/app.js";
import { TimeZoneContext, deviceTimeZone, isKnownZone, mirrorTimeZone, zoneDecision, type TimeZoneState } from "./time-zone.js";

/** A message picked to be answered: its id, who said it and a preview, for the composer's chip. */
interface ReplyTarget {
  readonly id: string;
  readonly author: string;
  readonly preview: string;
}

/** The chat on screen and whose it is, so a page or an event meant for a chat just left is dropped. */
interface Shown {
  readonly agentId: string | null;
  readonly chat: ChatState;
}

type ChatAction =
  | { type: "reset" }
  | { type: "open"; agentId: string; chat: ChatState }
  | { type: "page"; agentId: string; page: ChatResponse }
  | { type: "older"; agentId: string; page: ChatResponse }
  | { type: "event"; agentId: string; seq: number; event: ServerEvent };

function chatReducer(state: Shown, action: ChatAction): Shown {
  if (action.type === "reset") return { agentId: null, chat: emptyChat };
  if (action.type === "open") return { agentId: action.agentId, chat: action.chat };
  if (action.agentId !== state.agentId) return state;
  switch (action.type) {
    case "page":
      return { ...state, chat: reconcile(state.chat, action.page) };
    case "older":
      return { ...state, chat: prependMessages(state.chat, action.page) };
    case "event": {
      const chat = applyEvent(state.chat, action.event, action.seq);
      return chat === state.chat ? state : { ...state, chat };
    }
  }
}

/** The browser's storage, or none where it is refused (a private window, a policy). */
function browserStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

const SELECTED_KEY = "ark-bot.selectedAgent";
const DETAILS_KEY = "ark-bot.detailsOpen";
const SEEN_KEY = "ark-bot.seen";
const UNREAD_KEY = "ark-bot.unread";
const SIDEBAR_WIDTH_KEY = "ark-bot.sidebarWidth";
const DETAILS_WIDTH_KEY = "ark-bot.detailsWidth";
const SIDEBAR_HIDDEN_KEY = "ark-bot.sidebarHidden";
/** How long the sidebar takes to slide away or back; the grid's transition is this long. */
const SIDEBAR_SLIDE_MS = 240;
/** How far the strip on the window's left edge is dragged before the sidebar comes out. */
const EDGE_DRAG_PX = 40;
/** How long a pane or a popup takes to leave; the CSS exit animations are this long. */
const EXIT_MS = 220;
/** How many bubbles a chat opens with, and how many more each scroll to the top brings. */
const PAGE = 60;

/** When the user last had each chat open, kept in the browser: the host does not know which chat is on screen. */
function loadSeen(): Record<string, number> {
  try {
    const parsed = JSON.parse(localStorage.getItem(SEEN_KEY) ?? "{}") as unknown;
    return typeof parsed === "object" && parsed != null ? Object.fromEntries(Object.entries(parsed).filter((pair): pair is [string, number] => typeof pair[1] === "number")) : {};
  } catch {
    // Anything but a map of times means it was never written, or not by us; nothing is unread then.
    return {};
  }
}

function loadForcedUnread(): Set<string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(UNREAD_KEY) ?? "[]") as unknown;
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : []);
  } catch {
    return new Set();
  }
}

/** A plain agent list as a roster: nothing said, nobody working. What the page shows until the stream's snapshot arrives. */
function rosterOf(list: readonly Agent[]): RosterAgent[] {
  return list.map((agent) => ({ ...agent, updatedAt: agent.createdAt, busy: false }));
}

export function App(): React.JSX.Element {
  const t = useText(APP);
  const [agents, setAgents] = useState<RosterAgent[]>([]);
  const [rosterStatus, setRosterStatus] = useState<ConnectionStatus>("connecting");
  const [sidebar, setSidebar] = useState<SidebarSettings>(EMPTY_SIDEBAR);
  // A phone opens on the roster, as WhatsApp does, so Back has somewhere to go; a screen with the roster beside the chat reopens the chat it had.
  const [selectedId, setSelectedId] = useState<string | null>(() => (layoutAt(window.innerWidth) === "phone" ? null : localStorage.getItem(SELECTED_KEY)));
  const [status, setStatus] = useState<ConnectionStatus>("closed");
  const [notice, setNotice] = useState<string | null>(null);
  const [permission, setPermission] = useState<NotificationPermissionState>(() => notificationPermission());
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** The zone the user picked in Settings, as the host keeps it; null until they pick one. */
  const [chosenZone, setChosenZone] = useState<string | null>(null);
  const [pluginsOpen, setPluginsOpen] = useState(false);
  /** The connect screen over the page: the packaged app changing which computer it talks to. */
  const [hostOpen, setHostOpen] = useState(false);
  /** The first-run tour over the page (components/Tour.tsx): open by itself once on a new workspace, or again from Settings. */
  const [tourOpen, setTourOpen] = useState(false);
  /** The settings as first read, and whether the roster's first full snapshot has arrived: what the tour and a new box's zone are decided on. */
  const [firstSettings, setFirstSettings] = useState<"loading" | "failed" | HostSettings>("loading");
  const [rosterReady, setRosterReady] = useState(false);
  const tourDecided = useRef(false);
  /** Set once the page has filled in a new box's zone, or found it should not; a zone the user picks sets it too. */
  const zoneDecided = useRef(false);
  // Open beside the chat where there is room; on a phone it lies over the chat, so it waits to be asked for.
  const [detailsOpen, setDetailsOpen] = useState(() => (layoutAt(window.innerWidth) === "wide" ? localStorage.getItem(DETAILS_KEY) !== "0" : false));
  const [detailsMode, setDetailsMode] = useState<DetailsMode>("details");
  /** The screen open large over the page, with take-over. */
  const [screenOpen, setScreenOpen] = useState(false);
  /**
   * Whether this host serves a screen at all (§6.17): `false` on a lite box,
   * which has no desktop, and the screen goes from the page entirely. `null`
   * until the host answers, and until then the pane shows as it always did,
   * so a full box never blinks its screen in.
   */
  const [hostScreen, setHostScreen] = useState<boolean | null>(null);
  /** The side panes' widths, dragged by their edges and kept in the browser. */
  const [sidebarWidth, setSidebarWidth] = useState(() => loadWidth(SIDEBAR_WIDTH, localStorage.getItem(SIDEBAR_WIDTH_KEY)));
  const [detailsWidth, setDetailsWidth] = useState(() => loadWidth(DETAILS_WIDTH, localStorage.getItem(DETAILS_WIDTH_KEY)));
  /** The sidebar put away on a wider screen, for the chat's whole width; a phone's roster is a screen of its own and never hides. */
  const [sidebarHidden, setSidebarHidden] = useState(() => browserStorage()?.getItem(SIDEBAR_HIDDEN_KEY) === "1");
  /** The grid slides only while the sidebar goes or comes: a transition left on would make a drag of its edge lag behind the pointer. */
  const [sidebarSliding, setSidebarSliding] = useState(false);
  const slideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setSidebarShown = useCallback((shown: boolean): void => {
    setSidebarHidden(!shown);
    browserStorage()?.setItem(SIDEBAR_HIDDEN_KEY, shown ? "0" : "1");
    setSidebarSliding(true);
    if (slideTimer.current != null) clearTimeout(slideTimer.current);
    slideTimer.current = setTimeout(() => setSidebarSliding(false), SIDEBAR_SLIDE_MS + 40);
  }, []);
  const edgeDrag = useRef<number | null>(null);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [seen, setSeen] = useState<Record<string, number>>(loadSeen);
  const [forcedUnread, setForcedUnread] = useState<Set<string>>(loadForcedUnread);
  /** Ticks each minute so the row times ("5m") stay honest without an event. */
  const [now, setNow] = useState(() => Date.now());
  const [shown, dispatch] = useReducer(chatReducer, { agentId: null, chat: emptyChat });
  const chat = shown.chat;
  /** A message the box took back because the account could not pay for the model: said once, and put back in the composer. */
  const toldWithdrawn = useRef<string | null>(null);
  const [restore, setRestore] = useState<{ readonly agentId: string; readonly text: string; readonly key: number } | null>(null);
  const withdrawn = chat.withdrawn;
  const withdrawnKey = withdrawn == null || shown.agentId == null ? null : `${shown.agentId}:${withdrawn.seq}`;
  useEffect(() => {
    if (withdrawn == null || withdrawnKey == null || toldWithdrawn.current === withdrawnKey) return;
    toldWithdrawn.current = withdrawnKey;
    // A cached chat still carries its last one; only a fresh one is news.
    if (Date.now() - withdrawn.at > 120_000) return;
    setNotice(withdrawn.message);
    setRestore({ agentId: shown.agentId ?? "", text: withdrawn.texts.join("\n\n"), key: withdrawn.seq });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per message taken back
  }, [withdrawnKey]);
  const account = useAccount();
  /**
   * The chats already shown (chat-cache.ts), so going back to one paints at
   * once. Kept per host and account; the page is remade when either changes.
   */
  const [chatCache] = useState(() => new ChatCache(browserStorage(), cacheScope(configuredHost() ?? window.location.origin, account?.session.account?.id)));
  /** What the user sent that no chat shows yet (outbox.ts), across chats: one sent here and left still lands. */
  const [outbox, setOutbox] = useState<Outgoing[]>([]);
  const outgoingCount = useRef(0);
  /** The page's yes-or-no dialog (ConfirmDialog.tsx), for this and everything under it, in place of the browser's confirm box. */
  const { confirm, dialog: confirmDialog } = useConfirmHost();
  /** The roster as the notification stream sees it, without re-subscribing on every change. */
  const agentsRef = useRef<RosterAgent[]>([]);
  agentsRef.current = agents;

  /**
   * A plain list, for the first paint and for a host running since before the
   * roster stream existed: rows already known from the stream keep what they
   * know, rows that are gone go.
   */
  const reloadAgents = useCallback(async (): Promise<RosterAgent[]> => {
    try {
      const list = await api.listAgents();
      let merged: RosterAgent[] = [];
      setAgents((current) => {
        merged = rosterOf(list).map((row) => current.find((known) => known.id === row.id) ?? row);
        return merged;
      });
      setNotice(null);
      return merged;
    } catch (error) {
      setNotice(text(APP).cannotReachBox(error instanceof Error ? error.message : String(error)));
      return [];
    }
  }, []);

  useEffect(() => {
    void reloadAgents().then((list) => {
      if (layoutAt(window.innerWidth) !== "phone" && list.length > 0 && !list.some((agent) => agent.id === selectedId)) setSelectedId(list[0]!.id);
    });
    api
      .settings()
      .then((settings) => {
        setSidebar(settings.sidebar ?? EMPTY_SIDEBAR);
        setChosenZone(settings.timeZone ?? null);
        setFirstSettings(settings);
      })
      .catch(() => {
        setSidebar(EMPTY_SIDEBAR);
        setFirstSettings("failed");
      });
    // Only on mount: later selection changes are user-driven.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadAgents]);

  // The sidebar renders from the roster stream and nothing else (ARCHITECTURE §5.5).
  useEffect(() => {
    const stream = subscribeRoster((event) => {
      setAgents((current) => applyRosterEvent(current, event));
      if (event.type === "roster") {
        setRosterReady(true);
        // The whole list: a chat kept for a bot that is no longer on it goes.
        chatCache.keepOnly(new Set(event.agents.map((agent) => agent.id)));
      }
      if (event.type === "agent.deleted") chatCache.forget(event.id);
    }, setRosterStatus);
    return () => stream.close();
  }, [chatCache]);

  /**
   * The tour's flag lives in the host's settings and nowhere else, so it is
   * kept per workspace: a fresh box opens the tour in any browser. A host
   * that refuses the key only means the tour may open once more.
   */
  const settleTour = useCallback((): void => {
    api.updateSettings({ onboarded: true }).catch(() => undefined);
  }, []);

  // Decided once per page, as soon as the settings and the roster can say: a new workspace gets the tour, an old one never does.
  useEffect(() => {
    if (tourDecided.current) return;
    const decision = tourDecision({ settings: firstSettings, roster: rosterReady ? agents : null });
    if (decision === "wait") return;
    tourDecided.current = true;
    if (decision === "show") setTourOpen(true);
    if (decision === "settle") settleTour();
  }, [firstSettings, rosterReady, agents, settleTour]);

  // Decided once per page as well: a new box takes this device's zone, so nobody is asked for one to begin with (time-zone.ts).
  useEffect(() => {
    if (zoneDecided.current) return;
    const device = deviceTimeZone();
    const decision = zoneDecision({ settings: firstSettings, roster: rosterReady ? agents : null, device });
    if (decision === "wait") return;
    zoneDecided.current = true;
    if (decision !== "apply") return;
    api
      .updateSettings({ timeZone: device })
      // A zone the user picked meanwhile went out after this one, and stands.
      .then((settings) => setChosenZone((current) => current ?? settings.timeZone ?? null))
      // Nothing is lost: the box stays unset, Settings says so, and the next visit tries again.
      .catch(() => undefined);
  }, [firstSettings, rosterReady, agents]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  /**
   * A chat opens on its newest page, then follows the event stream from the
   * moment that page was read (ARCHITECTURE §5.5): nothing older is replayed,
   * and older bubbles come a page at a time when the user scrolls up. One
   * shown before paints at once from the cache while that page is read; the
   * page replaces it if anything moved meanwhile.
   */
  useEffect(() => {
    if (selectedId == null) return;
    localStorage.setItem(SELECTED_KEY, selectedId);
    const kept = chatCache.get(selectedId);
    dispatch({ type: "open", agentId: selectedId, chat: kept ?? emptyChat });
    setDetailsMode("details");
    setScreenOpen(false);
    // Where the pane lies over the chat, opening a chat means the chat: the pane from the last one goes.
    if (layoutAt(window.innerWidth) !== "wide") setDetailsOpen(false);
    let subscription: { close(): void } | null = null;
    let cancelled = false;
    const follow = (after: number): void => {
      subscription = subscribe(selectedId, after, (seq, event) => dispatch({ type: "event", agentId: selectedId, seq, event }), setStatus);
    };
    api
      .chat(selectedId, { limit: PAGE })
      .then((page) => {
        if (cancelled) return;
        dispatch({ type: "page", agentId: selectedId, page });
        follow(page.eventSeq);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // No page to start from: carry on from the kept copy's cursor, or replay the log from the beginning, as before pages existed.
        setNotice(text(APP).couldNotReadChat(error instanceof Error ? error.message : String(error)));
        follow(kept?.lastSeq ?? 0);
      });
    return () => {
      cancelled = true;
      subscription?.close();
    };
  }, [selectedId, chatCache]);

  // The chat on screen, as it moves, is what the cache holds for it.
  useEffect(() => {
    if (shown.agentId != null) chatCache.put(shown.agentId, shown.chat);
  }, [shown, chatCache]);

  /**
   * Written down when the page is hidden: a phone may put the app away and
   * never bring this page back, and the next one opens on what was kept. Not
   * on unmount, which is also signing out, when nothing is to stay.
   */
  useEffect(() => {
    const save = (): void => chatCache.save();
    const hidden = (): void => {
      if (document.visibilityState === "hidden") save();
    };
    document.addEventListener("visibilitychange", hidden);
    window.addEventListener("pagehide", save);
    return () => {
      document.removeEventListener("visibilitychange", hidden);
      window.removeEventListener("pagehide", save);
      chatCache.close();
    };
  }, [chatCache]);

  const loadMore = useCallback(async (): Promise<void> => {
    const before = oldestSeq(chat);
    if (selectedId == null || before == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await api.chat(selectedId, { limit: PAGE, before });
      dispatch({ type: "older", agentId: selectedId, page });
    } catch (error) {
      setNotice(text(APP).couldNotReadEarlier(error instanceof Error ? error.message : String(error)));
    } finally {
      setLoadingMore(false);
    }
  }, [selectedId, chat, loadingMore]);

  // An agent first met this session starts read: what happened before the page opened is not news.
  useEffect(() => {
    if (!agents.some((agent) => seen[agent.id] == null)) return;
    const at = Date.now();
    setSeen((current) => {
      const next = { ...current };
      for (const agent of agents) if (next[agent.id] == null) next[agent.id] = at;
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
      return next;
    });
  }, [agents, seen]);

  // The chat on screen is read as it arrives; nothing in it is unread.
  useEffect(() => {
    if (selectedId == null) return;
    const at = Date.now();
    setSeen((current) => {
      const next = { ...current, [selectedId]: at };
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
      return next;
    });
    setForcedUnread((current) => {
      if (!current.has(selectedId)) return current;
      const next = new Set(current);
      next.delete(selectedId);
      localStorage.setItem(UNREAD_KEY, JSON.stringify([...next]));
      return next;
    });
  }, [selectedId, chat.messages.length]);

  useEffect(() => {
    // A phone's choice is about room, not preference; only a wide window remembers it.
    if (layoutAt(window.innerWidth) === "wide") localStorage.setItem(DETAILS_KEY, detailsOpen ? "1" : "0");
  }, [detailsOpen]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth));
    localStorage.setItem(DETAILS_WIDTH_KEY, String(detailsWidth));
  }, [sidebarWidth, detailsWidth]);

  /**
   * One stream for every agent, so an agent that is not on screen can still
   * reach the user. Nothing is raised while the tab is visible, and nothing
   * for the agent they are already reading.
   */
  useEffect(() => {
    if (permission !== "granted") return;
    const stream = subscribeNotifications((notification) => {
      if (!document.hidden && notification.agentId === selectedId) return;
      const agent = agentsRef.current.find((candidate) => candidate.id === notification.agentId);
      if (agent?.config.notify === false) return;
      raise(notification, (agentId) => setSelectedId(agentId));
    });
    return () => stream.close();
  }, [permission, selectedId]);

  /**
   * The packaged app's push (push.ts): the phone's token goes to the host at
   * every start, as the same token again is only a refresh; a tapped
   * notification opens its bot, whether the page was up or is starting now.
   */
  useEffect(() => {
    // Every way push can be off in the shell is said on the page, not only in a console nobody sees on a phone.
    const state = shellPushState(window as PushWindow);
    if (state === "browser") return;
    if (state === "no-plugin") {
      setNotice(text(APP).pushNoPlugin);
      return;
    }
    const push = shellPush(window as PushWindow);
    if (push == null) return;
    let cancelled = false;
    push
      .register()
      .then(async (registration) => {
        if (cancelled) return;
        if (registration.token == null) {
          setNotice(text(APP).pushNoFirebase);
          return;
        }
        await api.registerPush(registration.token, registration.device);
        // Said once per phone, the first time the host has it; every later start is a silent refresh.
        if (!cancelled && firstRegistration(typeof localStorage === "undefined" ? null : localStorage, registration.token)) setNotice(text(APP).pushRegistered(registration.device));
      })
      // A host without the route (older than push), or one that is away.
      .catch((error: unknown) => setNotice(text(APP).pushNotRegistered(error instanceof Error ? error.message : String(error))));
    void push.pendingAgent().then((pending) => {
      if (!cancelled && pending.agentId != null) setSelectedId(pending.agentId);
    });
    const listening = Promise.resolve(push.addListener("openAgent", (event) => setSelectedId(event.agentId)));
    return () => {
      cancelled = true;
      void listening.then((listener) => listener.remove());
    };
  }, []);

  // The shell stays quiet about the bot on screen, as the browser does for a visible tab; a page put away has none on screen.
  useEffect(() => {
    const push = shellPush(window as PushWindow);
    if (push == null) return;
    const tell = (): void => {
      void push.setVisibleAgent({ agentId: document.hidden ? null : selectedId }).catch(() => undefined);
    };
    tell();
    document.addEventListener("visibilitychange", tell);
    return () => document.removeEventListener("visibilitychange", tell);
  }, [selectedId]);

  // Asked once: a host does not grow or lose a desktop while the page is open.
  useEffect(() => {
    let cancelled = false;
    void api.vnc().then(
      (config) => { if (!cancelled) setHostScreen(config != null && config.screens.length > 0); },
      // A host that cannot be asked is not a host without a screen; leave the
      // page as it was and let the pane report the failure itself.
      () => undefined,
    );
    return () => { cancelled = true; };
  }, []);
  /** The screen is part of the page unless the host has said it serves none. */
  const screenAvailable = hostScreen !== false;

  const selected = agents.find((agent) => agent.id === selectedId) ?? null;

  /** This chat's messages on their way, less any whose own bubble has come back from the host. */
  const outgoing = useMemo(
    () => (shown.agentId == null || shown.agentId !== selectedId ? [] : unsettled(outbox.filter((one) => one.agentId === shown.agentId), chat.messages)),
    [outbox, shown.agentId, selectedId, chat.messages],
  );

  // Those whose bubble has come back are done with.
  useEffect(() => {
    const agentId = shown.agentId;
    if (agentId == null) return;
    setOutbox((current) => {
      const mine = current.filter((one) => one.agentId === agentId);
      if (mine.length === 0) return current;
      const waiting = new Set(unsettled(mine, chat.messages).map((one) => one.id));
      return waiting.size === mine.length ? current : current.filter((one) => one.agentId !== agentId || waiting.has(one.id));
    });
  }, [shown.agentId, chat.messages]);
  // A group is a room: it has no session of its own and no box, so the outline and the screen belong to its members.
  const isGroup = selected?.kind === "group";
  const agentState = agentStatus(chat);
  const usingDesktop = agentUsingDesktop(chat);
  // The agent asking for the box: the card in the chat and the banner over the screen are the same request.
  const handoff = pendingHandoff(chat.messages);

  // The pane comes up on its own when the agent starts using the desktop, as the prototype opens its computer preview.
  useEffect(() => {
    if (usingDesktop && !isGroup) setDetailsOpen(true);
  }, [usingDesktop, isGroup]);

  // A request for the box opens the screen large: the user is being asked to drive it.
  useEffect(() => {
    if (handoff != null) setScreenOpen(true);
  }, [handoff?.id]);

  /** An agent's face by id; the roster lives here, so the chat asks for it rather than carrying one. */
  const avatarFor = useCallback(
    (id: string, name: string, size?: number): React.JSX.Element => {
      const found = agents.find((agent) => agent.id === id);
      return <Avatar id={id} name={found?.name ?? name} avatar={found?.config.avatar} {...(size == null ? {} : { size })} />;
    },
    [agents],
  );

  /** Who an @ can name here: the other agents, or in a room its members and everyone. */
  const mentionables = useMemo(() => (selected == null ? [] : mentionablesFor(selected, agents)), [selected, agents]);

  /** One more agent, with the profile written for it; or, with members, a room. */
  const createAgent = async (request: CreateAgentRequest): Promise<void> => {
    const agent = await api.createAgent(request);
    // The stream adds the row; the list is asked too, for a host without the stream.
    await reloadAgents();
    setSelectedId(agent.id);
  };

  /**
   * The tour's last step: the Bot Hiring Manager's chat, opened on the
   * user's first message to it, the job their first bot is for. It asks its
   * questions and makes the bot from there. A send that fails stays on the
   * tour's form, to be tried again.
   */
  const hireFirstBot = async (managerId: string, message: string): Promise<void> => {
    await api.postMessage(managerId, { text: message });
    setSelectedId(managerId);
  };

  /** Taken or skipped, the tour is over for this workspace. */
  const closeTour = (): void => {
    setTourOpen(false);
    settleTour();
  };

  const updateAgent = async (request: UpdateAgentRequest): Promise<void> => {
    if (selected == null) return;
    await api.updateAgent(selected.id, request);
    await reloadAgents();
  };

  const deleteAgent = async (agent: RosterAgent): Promise<void> => {
    const group = agent.kind === "group";
    if (!(await confirm({ title: group ? t.deleteGroupTitle(agent.name) : t.deleteBotTitle(agent.name), message: group ? t.deleteGroupMessage : t.deleteBotMessage, action: t.delete, danger: true }))) return;
    await api.deleteAgent(agent.id);
    chatCache.forget(agent.id);
    setOutbox((current) => current.filter((one) => one.agentId !== agent.id));
    const list = await reloadAgents();
    if (agent.id === selectedId) setSelectedId(list.find((one) => one.id !== agent.id)?.id ?? null);
    if (list.length === 0) dispatch({ type: "reset" });
  };

  const duplicateAgent = async (agent: RosterAgent): Promise<void> => {
    try {
      const copy = await api.duplicateAgent(agent.id);
      await reloadAgents();
      setSelectedId(copy.id);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    }
  };

  /** The arrangement is host settings: every change goes there and comes back normalized. */
  const saveSidebar = (next: SidebarSettings): void => {
    setSidebar(next);
    api
      .updateSettings({ sidebar: next })
      .then((settings) => setSidebar(settings.sidebar ?? EMPTY_SIDEBAR))
      .catch((error: unknown) => setNotice(t.couldNotSaveSidebar(error instanceof Error ? error.message : String(error))));
  };

  const markUnread = (id: string): void => {
    if (id === selectedId) setSelectedId(null);
    setForcedUnread((current) => {
      const next = new Set(current).add(id);
      localStorage.setItem(UNREAD_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  /** The message the next one answers (§ reply context), per chat as its draft is: kept through a visit to another bot, cleared on send or dismissal. */
  const [replies, setReplies] = useState<Readonly<Record<string, ReplyTarget>>>({});
  const replyTo = selected == null ? null : (replies[selected.id] ?? null);
  const setReplyTo = (target: ReplyTarget | null): void => {
    if (selected == null) return;
    const id = selected.id;
    setReplies(({ [id]: _was, ...rest }) => (target == null ? rest : { ...rest, [id]: target }));
  };

  /** Posts one outgoing message; whatever the box answers is written on it, and the chat draws it so. */
  const post = async (message: Outgoing): Promise<void> => {
    const mark = (status: Outgoing["status"], error?: string): void => setOutbox((current) => current.map((one) => (one.id === message.id ? { ...one, status, error } : one)));
    try {
      await api.postMessage(message.agentId, { text: message.text, ...(message.attachments.length === 0 ? {} : { attachments: [...message.attachments] }), ...(message.replyTo == null ? {} : { replyTo: message.replyTo }) });
      mark("sent");
      setNotice(null);
    } catch (error) {
      mark("failed", error instanceof Error ? error.message : String(error));
    }
  };

  /** The message shows in the chat at once, greyed until the box has it (outbox.ts). */
  const send = async (text: string, attachments: Attachment[], replyTo?: string): Promise<void> => {
    if (selected == null) return;
    // Its entry comes after the newest bubble known, on the page or in the roster, whichever is further on.
    const shownNewest = shown.agentId === selected.id ? (chat.messages.at(-1)?.seq ?? -1) : -1;
    outgoingCount.current += 1;
    const message: Outgoing = { id: `out-${outgoingCount.current}`, agentId: selected.id, text, attachments, replyTo, afterSeq: Math.max(shownNewest, selected.lastEntry?.seq ?? -1), status: "sending" };
    setOutbox((current) => [...current, message]);
    await post(message);
  };

  const retrySend = (id: string): void => {
    const message = outbox.find((one) => one.id === id);
    if (message == null || message.status !== "failed") return;
    setOutbox((current) => current.map((one) => (one.id === id ? { ...one, status: "sending", error: undefined } : one)));
    void post(message);
  };

  const discardSend = (id: string): void => setOutbox((current) => current.filter((one) => one.id !== id));

  const resolveApproval = async (approvalId: string, approved: boolean, allowRule?: string): Promise<void> => {
    try {
      await api.resolveApproval(approvalId, approved, allowRule);
      setNotice(null);
    } catch (error) {
      // A card answered elsewhere, expired, or raised before a restart: the event stream updates it; say why nothing happened.
      setNotice(error instanceof Error ? error.message : String(error));
    }
  };

  const submitForm = async (formId: string, values: Record<string, string>): Promise<void> => {
    if (selected == null) return;
    try {
      await api.submitForm(selected.id, formId, values);
      setNotice(null);
    } catch (error) {
      // The turn moved on, or the card timed out: the event stream has the truth.
      setNotice(error instanceof Error ? error.message : String(error));
    }
  };

  const cancelForm = async (formId: string): Promise<void> => {
    if (selected == null) return;
    try {
      await api.cancelForm(selected.id, formId);
      setNotice(null);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    }
  };

  const submitLogin = async (loginId: string, request: SubmitLoginRequest): Promise<void> => {
    if (selected == null) throw new Error("Select the conversation for this sign-in.");
    await api.submitLogin(selected.id, loginId, request);
    setNotice(null);
  };

  const answerContact = async (entryId: string, request: AnswerContactRequest): Promise<void> => {
    if (selected == null) return;
    try {
      await api.answerContact(selected.id, entryId, request);
      setNotice(null);
    } catch (error) {
      // A booking the calendar refused is said on the card itself, which stays open.
      if ("meeting" in request) throw error;
      // Answered already, from another device: the event stream has the truth.
      setNotice(error instanceof Error ? error.message : String(error));
    }
  };

  const resolveHandoff = async (resolution: HandoffResolution): Promise<void> => {
    if (selected == null) return;
    try {
      await api.resolveHandoff(selected.id, resolution);
      setNotice(null);
    } catch (error) {
      // Answered elsewhere, or the agent moved on: the event stream has the truth.
      setNotice(error instanceof Error ? error.message : String(error));
    }
  };

  // Back, on a phone, closes what was opened last rather than leaving the app
  // (the layouts of panes.ts, which the stylesheet's breakpoints match: a
  // phone shows one of roster and chat, a narrow screen lays the side pane
  // over the chat; on a wide screen the pane is furniture and Back leaves it
  // alone). A foldable moves between these as it opens and closes.
  const phone = useMedia(PHONE_QUERY);
  const sidebarAway = sidebarHidden && !phone;
  useBackClose(switcherOpen, () => setSwitcherOpen(false));
  // ⌘K or Ctrl+K opens the switcher from anywhere; ⌘B or Ctrl+B puts the sidebar away or brings it back.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (isSwitcherKey(event)) {
        event.preventDefault();
        setSwitcherOpen((open) => !open);
      } else if (isSidebarKey(event) && !phone) {
        event.preventDefault();
        setSidebarShown(sidebarHidden);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phone, sidebarHidden, setSidebarShown]);
  const paneOverlays = useMedia(OVERLAY_QUERY);
  useBackClose(selected != null, () => setSelectedId(null), phone);
  useBackClose(detailsOpen && selected != null, () => setDetailsOpen(false), paneOverlays);
  useBackClose(detailsMode !== "details" && detailsOpen && selected != null, () => setDetailsMode("details"), paneOverlays);
  useBackClose(screenOpen && selected != null && !isGroup && screenAvailable, () => setScreenOpen(false));
  useBackClose(settingsOpen, () => setSettingsOpen(false));
  useBackClose(pluginsOpen, () => setPluginsOpen(false));
  useBackClose(hostOpen, () => setHostOpen(false));
  // Installed on a phone, Back at the roster warns first and leaves on the second press, as Android apps do.
  const leaveWarning = useLeaveWarning();
  const leaveToast = usePresence(leaveWarning, EXIT_MS);

  // Each pane and popup stays a moment after it is closed, to play its way out.
  const details = usePresence(detailsOpen && selected != null, EXIT_MS);
  const overlay = usePresence(screenOpen && selected != null && !isGroup && screenAvailable, EXIT_MS);
  const settingsModal = usePresence(settingsOpen, EXIT_MS);
  const plugins = usePresence(pluginsOpen, EXIT_MS);
  const hostModal = usePresence(hostOpen, EXIT_MS);
  const tour = usePresence(tourOpen, EXIT_MS);
  const switcher = usePresence(switcherOpen, EXIT_MS);
  /**
   * The zone every time on the page is shown in: the host's, set by the
   * first device to open a new box or picked in Settings, or this device's
   * while there is none. Picking one saves it on the host, where the agents
   * read it too.
   */
  const zone = chosenZone != null && isKnownZone(chosenZone) ? chosenZone : deviceTimeZone();
  mirrorTimeZone(zone);
  const timeZone = useMemo<TimeZoneState>(
    () => ({
      zone,
      chosen: chosenZone,
      choose: async (next) => {
        // The user's own pick: a new box's zone is no longer this page's to fill in.
        zoneDecided.current = true;
        const settings = await api.updateSettings({ timeZone: next });
        setChosenZone(settings.timeZone ?? null);
      },
    }),
    [zone, chosenZone],
  );
  const paneStyle = { "--sidebar-width": `${sidebarWidth}px`, "--details-width": `${detailsWidth}px` } as React.CSSProperties;

  const headline = agentState.awaitingApproval ? t.waitingForApproval : agentState.composing ? t.typing : agentState.activity != null ? `${agentState.activity[0]!.toUpperCase()}${agentState.activity.slice(1)}…` : agentState.working ? (chat.busy ? t.working : t.backgroundTask) : null;
  const subtitle = headline ?? (selected?.kind === "group" ? t.members(selected.members.length) : "");
  const streamState = status === "connecting" ? t.streamConnecting : status === "open" ? t.streamOpen : status === "reconnecting" ? t.streamReconnecting : t.streamClosed;
  const role = selected?.config.title?.trim() ?? "";

  return (
    <ConfirmProvider value={confirm}>
    <TimeZoneContext.Provider value={timeZone}>
    <div className={`app${selected != null ? " has-chat" : ""}${detailsOpen && selected != null ? " with-details" : ""}${sidebarAway ? " sidebar-hidden" : ""}${sidebarSliding ? " sidebar-sliding" : ""}`} style={paneStyle}>
      <AvatarShapeDefs />
      <Sidebar
        agents={agents}
        sidebar={sidebar}
        selectedId={selectedId}
        seen={seen}
        forcedUnread={forcedUnread}
        now={now}
        status={rosterStatus}
        onSelect={setSelectedId}
        onCreate={createAgent}
        onDelete={deleteAgent}
        onDuplicate={duplicateAgent}
        onEditProfile={(id) => {
          setSelectedId(id);
          setDetailsOpen(true);
          // The chat's own effect resets the mode on a switch; the profile wins after it.
          setTimeout(() => setDetailsMode("profile"), 0);
        }}
        onMarkUnread={markUnread}
        onTogglePinned={(id) => saveSidebar(togglePinned(sidebar, id))}
        onReorderPinned={(id, targetId, position) => saveSidebar(reorderPinned(sidebar, id, targetId, position))}
        onMoveToSection={(id, sectionId) => saveSidebar(moveToSection(sidebar, id, sectionId))}
        onNewSection={(name, agentId) => saveSidebar(addSection(sidebar, `s-${Date.now().toString(36)}`, name, agentId))}
        onRenameSection={(id, name) => saveSidebar(renameSection(sidebar, id, name))}
        onRemoveSection={(id) => saveSidebar(removeSection(sidebar, id))}
        onReorderSection={(id, targetId, position) => saveSidebar(reorderSection(sidebar, id, targetId, position))}
        onToggleHidden={(id) => saveSidebar(toggleHidden(sidebar, id))}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenPlugins={() => setPluginsOpen(true)}
        onOpenHost={bundledShell() ? () => setHostOpen(true) : undefined}
        onCollapse={phone ? undefined : () => setSidebarShown(false)}
        away={sidebarAway}
      />
      {sidebarAway ? (
        // The window's left edge while the sidebar is away: a click, or a pull to the right, brings it back.
        <div
          className="sidebar-edge"
          role="button"
          tabIndex={-1}
          aria-label={t.showSidebar}
          title={t.showSidebarTitle(shortcutLabel("B"))}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            edgeDrag.current = event.clientX;
          }}
          onPointerMove={(event) => {
            if (edgeDrag.current != null && event.clientX - edgeDrag.current >= EDGE_DRAG_PX) {
              edgeDrag.current = null;
              setSidebarShown(true);
            }
          }}
          onPointerUp={() => {
            if (edgeDrag.current != null) setSidebarShown(true);
            edgeDrag.current = null;
          }}
          onPointerCancel={() => {
            edgeDrag.current = null;
          }}
        />
      ) : (
        <Resizer label={t.sidebarWidth} value={sidebarWidth} bounds={SIDEBAR_WIDTH} grows="right" onChange={setSidebarWidth} />
      )}
      <main className="main">
        {selected == null ? (
          <div className="empty welcome">
            {sidebarAway ? (
              <div className="welcome-corner">
                <button type="button" className="icon show-sidebar" onClick={() => setSidebarShown(true)} aria-label={t.showSidebar} title={t.showSidebarTitle(shortcutLabel("B"))}>
                    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                      <rect x="2.5" y="3.5" width="15" height="13" rx="2.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
                      <path d="M7.5 3.5v13M11.5 8l2 2-2 2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
              </div>
            ) : null}
            <h2>ark-bot</h2>
            <p>{t.welcome}</p>
          </div>
        ) : (
          <>
            {/* The chat's own column: its header, a notice, the messages. The side pane stands beside it from the top of the window down. */}
            <div className="chat-column">
              <header className="topbar">
                <button type="button" className="icon back" onClick={() => setSelectedId(null)} aria-label={t.backToBots} title={t.back}>
                  ‹
                </button>
                {sidebarAway ? (
                  <button type="button" className="icon show-sidebar" onClick={() => setSidebarShown(true)} aria-label={t.showSidebar} title={t.showSidebarTitle(shortcutLabel("B"))}>
                    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                      <rect x="2.5" y="3.5" width="15" height="13" rx="2.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
                      <path d="M7.5 3.5v13M11.5 8l2 2-2 2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                ) : null}
                <span className={`agent-face-wrap topbar-face${agentState.working ? " working" : ""}`}>
                  <Avatar id={selected.id} name={selected.name} avatar={selected.config.avatar} size={34} />
                  {agentState.working ? <span className="work-ring" aria-hidden="true" /> : null}
                </span>
                <div className="title" title={selected.config.description ?? ""}>
                  <span className="title-line">
                    <h1>{selected.name}</h1>
                    {role.length > 0 ? <span className="tag">{role}</span> : null}
                  </span>
                  <span className={`subtitle${agentState.awaitingApproval ? " attention" : headline != null ? " live" : ""}`}>
                    <span className={agentState.awaitingApproval || headline == null ? undefined : "shimmer"}>{subtitle}</span>
                  </span>
                </div>
                <span className={`status status-${status}`} title={t.streamTitle(streamState)} aria-label={t.streamLabel(streamState)} />
                {permission === "granted" || permission === "unsupported" ? null : (
                  <button
                    type="button"
                    className="quiet"
                    onClick={() =>
                      void askForNotificationPermission().then((next) => {
                        setPermission(next);
                        // The browser asks once; after a refusal it stays quiet, so the way back is its own site settings.
                        if (next === "denied") setNotice(text(APP).notificationsBlockedNotice);
                      })
                    }
                    aria-label={permission === "denied" ? t.notificationsBlockedLabel : t.enableNotifications}
                    title={permission === "denied" ? t.notificationsBlockedTitle : t.enableNotificationsTitle}
                  >
                    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                      <path d="M10 2.5a4.5 4.5 0 0 0-4.5 4.5v3.2L4 13h12l-1.5-2.8V7A4.5 4.5 0 0 0 10 2.5ZM8 15.5a2 2 0 0 0 4 0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
                      {permission === "denied" ? <path d="M3 3l14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /> : null}
                    </svg>
                    <span className="label">{permission === "denied" ? t.notificationsBlocked : t.enableNotifications}</span>
                  </button>
                )}
                {/* Only while the pane is shut: once open, the pane's own » puts it away, so there is never a second button for it. */}
                {detailsOpen ? null : (
                  <button type="button" className="icon" onClick={() => setDetailsOpen(true)} aria-label={t.showSidePane} title={t.sidePaneTitle}>
                    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                      <rect x="2.5" y="3.5" width="15" height="13" rx="2.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
                      <path d="M12.5 3.5v13" stroke="currentColor" strokeWidth="1.6" />
                    </svg>
                  </button>
                )}
              </header>
              {notice != null ? (
                <div className="notice" role="alert">
                  {notice}
                  <button type="button" className="icon" onClick={() => setNotice(null)} aria-label={t.dismiss}>
                    ×
                  </button>
                </div>
              ) : null}
              <div className="chat-pane">
                <Chat key={selected.id} messages={chat.messages} outgoing={outgoing} onRetrySend={retrySend} onDiscardSend={discardSend} status={agentState} agentId={selected.id} agentName={selected.name} avatarFor={avatarFor} mentionables={mentionables} onOpenAgent={setSelectedId} hasMore={chat.hasMore} loadingMore={loadingMore} onLoadMore={() => void loadMore()} onSubmitForm={submitForm} onCancelForm={cancelForm} onSubmitLogin={submitLogin} onResolve={resolveApproval} onResolveHandoff={resolveHandoff} onTakeOver={() => setScreenOpen(true)} onAnswerContact={answerContact} onReply={setReplyTo} />
                {/* Its own key, not the chat's: siblings sharing one confuse React, which then keeps old chats on screen beside the new. */}
                <Composer key={`composer-${selected.id}`} draftKey={selected.id} agentName={selected.name} mentionables={mentionables} avatarFor={avatarFor} onSend={send} replyTo={replyTo} onClearReply={() => setReplyTo(null)} restore={restore?.agentId === selected.id ? restore : null} onRestored={() => setRestore(null)} />
              </div>
            </div>
            {details.mounted ? (
              <Details
                closing={details.closing}
                resizer={<Resizer label={t.sidePaneWidth} value={detailsWidth} bounds={DETAILS_WIDTH} grows="left" onChange={setDetailsWidth} />}
                agent={selected}
                agents={agents}
                mode={detailsMode}
                onMode={setDetailsMode}
                onClose={() => setDetailsOpen(false)}
                onSaveAgent={updateAgent}
                items={chat.items}
                busy={chat.busy}
                routinesRevision={chat.routinesRevision}
                skillsRevision={chat.skillsRevision}
                todosRevision={chat.todosRevision}
                memoryRevision={chat.memoryRevision}
                channelsRevision={chat.channelsRevision}
                context={chat.context}
                screen={{ inUse: usingDesktop, cursor: chat.cursor, screens: chat.screens, handoff, onResolveHandoff: resolveHandoff, open: screenOpen, onOpen: () => setScreenOpen(true), available: screenAvailable }}
              />
            ) : null}
          </>
        )}
      </main>
      {overlay.mounted && selected != null ? (
        <div className={`screen-overlay${overlay.closing ? " closing" : ""}`} role="dialog" aria-label={t.computerOf(selected.name)}>
          <Computer agentName={selected.name} inUse={usingDesktop} cursor={chat.cursor} screens={chat.screens} handoff={handoff} onResolveHandoff={resolveHandoff} onClose={() => setScreenOpen(false)} />
        </div>
      ) : null}
      {leaveToast.mounted ? (
        <div className={`toast${leaveToast.closing ? " closing" : ""}`} role="status" aria-live="polite">
          {t.pressBackAgain}
        </div>
      ) : null}
      {settingsModal.mounted ? (
        <div className={`modal-backdrop${settingsModal.closing ? " closing" : ""}`} onClick={() => setSettingsOpen(false)}>
          <div className="modal settings-modal" role="dialog" aria-label={t.settings} onClick={(event) => event.stopPropagation()}>
            <Settings
              onClose={() => setSettingsOpen(false)}
              onTour={() => {
                setSettingsOpen(false);
                setTourOpen(true);
              }}
            />
          </div>
        </div>
      ) : null}
      {plugins.mounted ? (
        <div className={`modal-backdrop${plugins.closing ? " closing" : ""}`} onClick={() => setPluginsOpen(false)}>
          <div className="modal plugins-modal" role="dialog" aria-label={t.plugins} onClick={(event) => event.stopPropagation()}>
            <Plugins onClose={() => setPluginsOpen(false)} />
          </div>
        </div>
      ) : null}
      {hostModal.mounted ? (
        <div className={`modal-backdrop${hostModal.closing ? " closing" : ""}`} onClick={() => setHostOpen(false)}>
          <div className="modal" role="dialog" aria-label={t.host} onClick={(event) => event.stopPropagation()}>
            <Connect current={configuredHost()} onClose={() => setHostOpen(false)} />
          </div>
        </div>
      ) : null}
      {switcher.mounted ? <Switcher agents={agents} sidebar={sidebar} selectedId={selectedId} closing={switcher.closing} onOpen={setSelectedId} onClose={() => setSwitcherOpen(false)} /> : null}
      {/* Only a host that has said it has a desktop is described as having one: until then, or on a lite box, the tour promises a terminal, files and the web. */}
      {tour.mounted ? <Tour screen={hostScreen === true} agents={agents} onHire={hireFirstBot} onClose={closeTour} closing={tour.closing} /> : null}
    </div>
    </TimeZoneContext.Provider>
      {confirmDialog}
    </ConfirmProvider>
  );
}
